/**
 * Serial handling on the client. The plaintext serial is only ever SENT (the
 * backend hashes and masks it); the UI shows `serial_masked`. Normalisation and
 * masking mirror models/InventoryUnit.model.js so a scan can be compared with
 * the stored mask.
 */

/** Scanner/keyboard input → canonical form: trimmed, upper-case, no spaces or dashes. */
export function normalizeSerial(value) {
  return String(value == null ? '' : value).trim().toUpperCase().replace(/[\s-]+/g, '');
}

/** Same rule as the backend: keep the first 2 and last 3 characters. */
export function maskSerial(value) {
  const s = normalizeSerial(value);
  if (s.length <= 5) return '*'.repeat(s.length);
  return s.slice(0, 2) + '*'.repeat(s.length - 5) + s.slice(-3);
}

/** A plausible device serial: 5–30 letters/digits after normalisation (phones and tablets use an IMEI). */
export function isValidSerial(value) {
  return /^[A-Z0-9]{5,30}$/.test(normalizeSerial(value));
}

/**
 * Does a scanned serial match the unit picked for an order?
 * Exact comparison when the plaintext is known (fixture orders); otherwise the
 * scan must reproduce the stored mask (length, first 2, last 3). An empty scan
 * never matches.
 */
export function serialMatches(scan, unit) {
  const s = normalizeSerial(scan);
  if (!s || !unit) return false;
  if (unit.serial) return s === normalizeSerial(unit.serial);
  if (unit.serial_masked) {
    // The backend masks trim().toUpperCase() without stripping dashes; accept either form.
    const raw = String(scan).trim().toUpperCase();
    return maskSerial(s) === unit.serial_masked || backendMask(raw) === unit.serial_masked;
  }
  return false;
}

function backendMask(serial) {
  if (serial.length <= 5) return '*'.repeat(serial.length);
  return serial.slice(0, 2) + '*'.repeat(serial.length - 5) + serial.slice(-3);
}
